import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Apple, Fish, Milk, Wheat, AlertTriangle, Cookie, Package, Beef } from 'lucide-react';

const CLLDietPage = () => {
  const foodsToEat = [
    {
      title: 'Colourful Fruits & Vegetables',
      icon: Apple,
      color: 'green',
      content: [
        'Fruits and vegetables offer unique benefits for people with CLL. The skin is rich in fibre to support digestion and healthy body weight. Natural fibres also feed beneficial gut bacteria, which plays an important role in immune function.',
        'Eating a variety of colours provides diverse vitamins and minerals, including iron. Iron deficiency can occur with CLL and may worsen fatigue.',
        'Good plant sources of iron include lentils, beans, pumpkin seeds, and tomato products (canned, stewed, or sun-dried).',
        'Vitamin C helps your body absorb iron from plants. Try adding lemon juice to cooked greens or tossing capsicum into a stir-fry.',
      ],
      tip: 'Always wash raw fruits and vegetables thoroughly. Food safety is especially important if your immune system is compromised.'
    },
    {
      title: 'Lean Proteins',
      icon: Fish,
      color: 'blue',
      content: [
        'Protein is essential for maintaining muscle mass and immune function. However, the saturated fats in high-fat or processed red meats make them less ideal choices.',
        'Plant-based proteins like beans, legumes, nuts, and tofu are excellent options. Seafood, poultry, and eggs are also good sources of quality protein with less saturated fat than red meat.',
        'These protein-rich foods help support iron levels, bone and muscle health, and immune function.'
      ],
      tip: 'People with CLL can be more prone to infection, so ensuring adequate protein intake helps support your body\'s defences.'
    },
    {
      title: 'Low-Fat Dairy or Alternatives',
      icon: Milk,
      color: 'purple',
      content: [
        'Low-fat milk, cheese, and yoghurt provide calcium and protein. Choosing lower-fat options can help avoid heartburn that some people experience during treatment.',
        'If you\'re struggling to maintain weight, higher-fat dairy may be a good choice to increase calories.',
        'Non-dairy alternatives like almond or soy milk are usually fortified with calcium. Check the nutrition label to compare calcium content with cow\'s milk.'
      ],
      tip: 'If you have trouble digesting dairy, many alternatives provide similar nutritional benefits.'
    },
    {
      title: 'Whole Grains',
      icon: Wheat,
      color: 'amber',
      content: [
        'Carbohydrates provide quick energy and can be comforting. Adding whole grains promotes heart health, improves blood sugar control, and offers other benefits.',
        'If you\'re dealing with bloating or digestive issues from CLL or treatment, high-fibre whole grains can help you stay regular.',
        'Good options include wholemeal bread, brown rice, oatmeal, quinoa, and buckwheat.',
        'Whole grains provide fibre, vitamin E, B vitamins, and beneficial antioxidants.'
      ],
      tip: 'Refined grains like white rice and pasta can still be part of a balanced diet - whole grains are an addition, not a replacement.'
    }
  ];

  const foodsToAvoid = [
    {
      title: 'High-Sugar Foods & Drinks',
      icon: Cookie,
      color: 'red',
      content: [
        'Sugar can cause energy highs and lows that worsen CLL-related fatigue. Added sugar is often hidden in foods that don\'t taste sweet - like pasta sauce, flavoured yoghurt, and frozen meals.',
        'Check food labels for added sugar content. If you\'re unsure, ask your healthcare team about seeing a dietitian.',
        'Cooking at home when you have energy gives you more control over ingredients.',
        'If you\'re losing weight and sweets are all you can tolerate, choose nutrient-dense sweet foods - like fruit in a protein smoothie or vegetables with hummus.'
      ]
    },
    {
      title: 'Ultra-Processed Foods',
      icon: Package,
      color: 'orange',
      content: [
        'Research has linked highly processed foods to higher inflammation levels. These foods are often high in added sugar, salt, fats, and additives.',
        'Scientists are still learning how certain additives affect gut bacteria and immunity.',
        'Eating more processed foods can also displace nutritious foods your body needs to function well.'
      ]
    },
    {
      title: 'Certain Raw Foods',
      icon: Beef,
      color: 'slate',
      content: [
        'CLL affects the immune system, making you more vulnerable to foodborne illness.',
        'Cook foods to safe temperatures and prepare them properly. Avoid raw meat, runny eggs, and raw seafood.',
        'Unpasteurised dairy and certain soft cheeses can pose a risk. These foods may contain bacteria that could make you unwell.'
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-50 border-green-200 text-green-700',
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      amber: 'bg-amber-50 border-amber-200 text-amber-700',
      red: 'bg-red-50 border-red-200 text-red-700',
      orange: 'bg-orange-50 border-orange-200 text-orange-700',
      slate: 'bg-slate-100 border-slate-300 text-slate-700'
    };
    return colors[color] || colors.slate;
  };

  const getIconBgClasses = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      amber: 'bg-amber-100 text-amber-600',
      red: 'bg-red-100 text-red-600',
      orange: 'bg-orange-100 text-orange-600',
      slate: 'bg-slate-200 text-slate-600'
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/learn"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
        data-testid="back-to-learn-link"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Learn
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" data-testid="cll-diet-title">
          Eating Well with CLL
        </h1>
        
        {/* Introduction */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Nourishing your body during your CLL journey
          </h2>
          
          <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              After a CLL diagnosis, eating well becomes an important part of feeling your best. The stress of diagnosis, symptoms of leukaemia, and treatment side effects can all affect your wellbeing and energy levels.
            </p>
            
            <p>
              While there's no official "CLL diet," making thoughtful food choices is a positive way to support your overall health. This guide offers practical suggestions to consider on your next trip to the shops.
            </p>
            
            <p className="font-medium text-slate-900 pt-2 border-t border-green-200">
              Everyone is different. What works for one person may not work for another. Always discuss dietary changes with your healthcare team, especially if you have low blood counts or are undergoing treatment.
            </p>
          </div>
        </div>
      </div>

      {/* Key Takeaway */}
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-6" data-testid="key-takeaway">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Apple className="h-6 w-6 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-teal-900 mb-2">Key Takeaway</h3>
            <p className="text-teal-800">
              Eating well after a CLL diagnosis can help you feel your best and support your energy levels during treatment. Focus on whole foods, lean proteins, and plenty of colourful fruits and vegetables.
            </p>
          </div>
        </div>
      </div>

      {/* Foods to Eat Section */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <span className="text-3xl">+</span>
          Foods to Include
        </h2>
        
        <p className="text-slate-600 mb-6">
          Rather than focusing on what to cut out, consider adding these nourishing foods to your diet. You might discover new favourites along the way.
        </p>

        <div className="space-y-6">
          {foodsToEat.map((food, index) => {
            const Icon = food.icon;
            return (
              <div 
                key={index} 
                className={`rounded-2xl border p-6 ${getColorClasses(food.color)}`}
                data-testid={`food-to-eat-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${getIconBgClasses(food.color)}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">
                      {food.title}
                    </h3>
                    <div className="space-y-3">
                      {food.content.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-slate-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {food.tip && (
                      <div className="mt-4 pt-4 border-t border-current/20">
                        <p className="text-sm font-medium text-slate-800">
                          <span className="font-semibold">Tip:</span> {food.tip}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Foods to Limit Section */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          Foods to Limit
        </h2>
        
        <p className="text-slate-600 mb-6">
          Unless your doctor advises otherwise, no foods are completely off-limits. However, limiting certain foods may help reduce symptoms and prevent additional health issues.
        </p>

        <div className="space-y-6">
          {foodsToAvoid.map((food, index) => {
            const Icon = food.icon;
            return (
              <div 
                key={index} 
                className={`rounded-2xl border p-6 ${getColorClasses(food.color)}`}
                data-testid={`food-to-avoid-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${getIconBgClasses(food.color)}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-4">
                      {food.title}
                    </h3>
                    <div className="space-y-3">
                      {food.content.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-slate-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Medication Warning */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-6" data-testid="medication-warning">
        <div className="flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-2">Important: Medication Interactions</h3>
            <p className="text-amber-800">
              Some foods can interact with CLL medications. For example, people taking BTK inhibitors should avoid grapefruit and Seville oranges (and their juices) as these can increase drug levels in your body. Always check with your healthcare team about any food restrictions related to your specific treatment.
            </p>
          </div>
        </div>
      </div>

      {/* Talk to Your Doctor */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl font-semibold text-blue-900 mb-4">
          Talk to Your Healthcare Team
        </h2>
        <div className="space-y-3 text-blue-800">
          <p>
            When in doubt, ask your healthcare team what's safe to eat. They may advise stricter food safety measures depending on your condition and treatment.
          </p>
          <p>
            Your doctor can also tell you if specific foods should be avoided while taking certain CLL medications.
          </p>
          <p>
            If you'd like personalised dietary advice, ask about seeing a registered dietitian who understands CLL and cancer care.
          </p>
        </div>
      </div>

      {/* Navigation Links */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <Link
          to="/integrative-health/food-nutrition"
          className="flex-1 text-center px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
          data-testid="view-food-profiles-link"
        >
          Explore Food Profiles
        </Link>
        <Link
          to="/integrative-health/food-safety"
          className="flex-1 text-center px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium border border-slate-200"
          data-testid="view-food-safety-link"
        >
          Food Safety Guide
        </Link>
      </div>
    </div>
  );
};

export default CLLDietPage;
